import Cookies from "js-cookie";
import { dedupeConcurrentRequest } from "@/app/_utils/dedupeConcurrentRequest";
import { getCsrfToken } from "@/app/_utils/csrf";

class OrderService {
  static instance: OrderService;

  get csrfToken() { return getCsrfToken(); }
  get tenant() { return Cookies.get("tenant") ?? ""; }
  baseUrl = `${import.meta.env.VITE_API_BASE_URL}`;
  private readonly inflight = new Map<string, Promise<unknown>>();

  getOrderSummaryAsync = async (
    start?: string,
    filters?: { search?: string; filter?: string; showIds?: string[] }
  ) => {
    const queryParams = new URLSearchParams();
    if (start) queryParams.append("start", start);
    if (filters?.search) queryParams.append("search", filters.search);
    if (filters?.filter && filters.filter !== "all") queryParams.append("filter", filters.filter);
    if (filters?.showIds && filters.showIds.length > 0) queryParams.append("shows", filters.showIds.join(","));

    return dedupeConcurrentRequest(
      this.inflight,
      `summary:${queryParams.toString()}`,
      async () => {
        const res = await fetch(`${this.baseUrl}orders/summary/?${queryParams}`, {
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });
        return await res.json();
      },
    );
  };

  getOrderAsync = async (id: string) => {
    const res = await fetch(`${this.baseUrl}orders/${id}/`, {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    return await res.json();
  };

  getOrdersAsync = async ({
    paginationModel,
    search,
    since,
    sortModel,
    filters,
    id,
    showIds,
    promo,
  }: {
    paginationModel?: any;
    sortModel?: any;
    search?: string | undefined;
    since?: any;
    filters?: any;
    id?: string;
    showIds?: string[];
    /** Promo code id — scopes results to orders that used that code. */
    promo?: string;
  }) => {
    const queryParams = new URLSearchParams();

    if (search) queryParams.append("search", search);
    if (since) queryParams.append("since", since);
    if (filters) queryParams.append("filter", filters);
    if (id) queryParams.append("id", id);
    if (showIds && showIds.length > 0) queryParams.append("shows", showIds.join(","));
    if (promo) queryParams.append("promo", promo);
    if (sortModel) queryParams.append("sort", JSON.stringify(sortModel));
    if (paginationModel) {
      let offest =
        paginationModel.page === 0
          ? 0
          : paginationModel.page * paginationModel.pageSize;
      queryParams.append("offset", offest.toString());
      queryParams.append("limit", paginationModel.pageSize.toString());
    }

    const listKey = `list:${queryParams.toString()}`;
    return dedupeConcurrentRequest(this.inflight, listKey, async () => {
      const res = await fetch(`${this.baseUrl}orders/?${queryParams}`, {
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      return await res.json();
    });
  };

  createOrderAsync = async (Order: any) => {
    const res = await fetch(`${this.baseUrl}orders/`, {
      method: "POST",
      body: JSON.stringify(Order),
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": this.csrfToken,
      },
      credentials: "include",
    });

    return await res.json();
  };

  updateOrderAsync = async (id: string, order: any) => {
    const res = await fetch(`${this.baseUrl}orders/${id}/`, {
      method: "PUT",
      body: JSON.stringify(order),
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": this.csrfToken,
      },
      credentials: "include",
    });

    return await res.json();
  };

  deleteOrderAsync = async (id: string) => {
    return await fetch(`${this.baseUrl}orders/${id}/`, {
      method: "DELETE",
      headers: { "X-CSRFToken": this.csrfToken },
      credentials: "include",
    });
  };

  handleRefund = async (id: number, quantity: number) => {
    const data = { quantity };

    const response = await fetch(`${this.baseUrl}orders/${id}/refund/`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": this.csrfToken,
      },
      body: JSON.stringify(data),
    });

    const responseData = await response.json();

    return {
      status: response.status,        // HTTP status code
      ok: response.ok,                // true if 200–299
      data: responseData,
    };
  };

  resendTicket = async (id: number, email: string) => {

    const response = await fetch(`${this.baseUrl}orders/${id}/resend-tickets/`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": this.csrfToken,
      },
      body: JSON.stringify({ email: email }),
    });

    const responseData = await response.json();

    return {
      status: response.status,        //  HTTP status code
      ok: response.ok,                //  true if 200–299
      data: responseData,
    };
  }


  sendReciept = async (id: number, email: string) => {

    const response = await fetch(`${this.baseUrl}orders/${id}/send-receipt/`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": this.csrfToken,
      },
      body: JSON.stringify({ email: email }),
    });
    const responseData = await response.json();

    return {
      status: response.status,        //  HTTP status code
      ok: response.ok,                // true if 200–299
      data: responseData,
    };


  }

 exportOrders = async ({ filters }: { filters?: any }) => {
  const queryParams = new URLSearchParams();
  if (filters) queryParams.append("filter", JSON.stringify(filters));

  const response = await fetch(`${this.baseUrl}orders/export/?${queryParams}`, {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`Export failed: ${response.statusText}`);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "orders_export.csv"; // adjust the filename if needed
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
}
export default new OrderService();
